import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  CardHeaderToolbar,
} from "../../../../_metronic/_partials/controls";
import { getAnswerByQuiz } from "../../../../api/answer";
import { getQuizById, getQuizzes, updateQuiz } from "../../../../api/quiz";
import { getGames } from "../../../../api/game";
import { uploadSingleFile } from "../../../../api/files";
import { alertError, alertSuccess } from "../../../../utils/logger";
import Table, {
  correctAnswerFormatter,
  timeFormatter,
} from "../../../components/tables/table";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Tooltip } from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import Editor from "../../../components/editor/Editor";
import { getDefaultQuizOptions, isScenario2Quiz, isScenario3Quiz, isScenario5Quiz, isScenario52Quiz, isScenario52Question2Quiz, explanationPreview } from "./getDefaultQuizOptions";
import { stripExplanationPrefix } from "./explanationHelpers";
import { SERVER_URL } from "../../../../api";

function buildImageUrl(value) {
  if (!value || typeof value !== "string") return null;
  if (value.startsWith("http")) return value;
  return `${SERVER_URL.replace(/\/$/, "")}/${value.replace(/^\//, "")}`;
}

function getData(answers) {
  let data = [];

  answers.forEach((answer) => {
    let elem = {
      group: answer.group?.fullName,
      score: answer?.score,
      time: answer.time,
    };
    data = data.concat(elem);
  });
  return data;
}

export default function ViewQuizPage() {
  const [games, setGames] = useState([]);
  const [selectedGameId, setSelectedGameId] = useState("");
  const [data, setData] = useState([]);
  const [quiz, setQuiz] = useState("");
  const [scenario, setScenario] = useState("");
  const [quizData, setQuizData] = useState(null);
  const [refresh, setRefresh] = useState(false);
  const [editPruebasOpen, setEditPruebasOpen] = useState(false);
  const [editingPruebasOptions, setEditingPruebasOptions] = useState([]);
  const [savingPruebas, setSavingPruebas] = useState(false);
  const [editingPruebaIndex, setEditingPruebaIndex] = useState(null);
  const [editingPruebaOne, setEditingPruebaOne] = useState(null);
  const quizId = useParams().id;
  const history = useHistory();

  const hasPruebasOptions = quizData?.options && Array.isArray(quizData.options) && quizData.options.length > 0;

  const openEditPruebas = () => {
    const opts = hasPruebasOptions
      ? quizData.options.map((o) => ({ ...o }))
      : getDefaultQuizOptions(quizData).map((o) => ({ ...o }));
    setEditingPruebasOptions(opts);
    setEditPruebasOpen(true);
  };

  const openEditPruebaOne = (index) => {
    const opt = editingPruebasOptions[index];
    if (!opt) return;
    setEditingPruebaIndex(index);
    setEditingPruebaOne({
      ...opt,
      explanation: stripExplanationPrefix(opt.explanation || ""),
    });
  };

  const closeEditPruebaOne = () => {
    setEditingPruebaIndex(null);
    setEditingPruebaOne(null);
  };

  const updateEditingPruebaOne = (field, value) => {
    setEditingPruebaOne((prev) => (prev ? { ...prev, [field]: value } : null));
  };

  const saveEditPruebaOne = () => {
    if (editingPruebaIndex == null || !editingPruebaOne) return;
    setEditingPruebasOptions((prev) => {
      const next = [...prev];
      next[editingPruebaIndex] = { ...editingPruebaOne };
      return next;
    });
    closeEditPruebaOne();
  };

  const savePruebasOptions = async () => {
    if (!quizId || !quizData) return;
    setSavingPruebas(true);
    try {
      const useScenario3 = isScenario3Quiz(quizData);
      const useScenario5 = isScenario5Quiz(quizData);
      const useScenario52 = isScenario52Quiz(quizData);
      const payload = {
        fullName: quizData.fullName,
        scenario: typeof quizData.scenario === "object" ? quizData.scenario._id : quizData.scenario,
        options: editingPruebasOptions.map((opt, idx) => ({
          id: opt.id,
          text: isScenario2Quiz(quizData) ? `Diagnóstico ${idx + 1}` : useScenario52 ? (opt.text || "") : useScenario5 ? String.fromCharCode(97 + idx) + ")" : (opt.text || ""),
          correct: !!opt.correct,
          explanation: stripExplanationPrefix(opt.explanation || "") || "",
          confirmExplanation: opt.confirmExplanation || "",
          ...(useScenario3 && { image: opt.image || "" }),
        })),
      };
      const res = await updateQuiz(quizId, payload);
      if (res.status === 200 && res.data) {
        setQuizData(res.data);
        setEditPruebasOpen(false);
        alertSuccess({ customMessage: "Pruebas guardadas." });
      }
    } catch (err) {
      alertError({ error: err, customMessage: "No se pudo guardar." });
    } finally {
      setSavingPruebas(false);
    }
  };

  useEffect(() => {
    getGames()
      .then((res) => {
        if (res.status === 200 && res.data?.length) {
          setGames(res.data);
          const defaultGame = res.data.find((g) => g.default) || res.data[0];
          if (!selectedGameId) setSelectedGameId(defaultGame._id);
        }
      })
      .catch(() => {});
  }, []);

  const defaultSorted = [
    {
      dataField: "score",
      order: "desc",
    },
  ];

  function headerFormatter(column, colIndex) {
    return typeof data[0]?.pts === "boolean" ? "Result" : "Points";
  }

  const columns = [
    { dataField: "group", text: "Group" },
    {
      dataField: "score",
      text: "Correct",
      align: "center",
      headerAlign: "center",
      formatter: correctAnswerFormatter,
      headerFormatter: headerFormatter,
      sort: true,
    },
    {
      dataField: "time",
      text: "Time",
      align: "center",
      headerAlign: "center",
      formatter: timeFormatter,
      sort: true,
    },
  ];

  useEffect(() => {
    getQuizById(quizId)
      .then((res) => {
        if (res.status === 200 && res.data) {
          const q = res.data;
          setQuizData(q);
          setQuiz(q.fullName);
          setScenario(q.scenario?.fullName || "");
          if (q.game) setSelectedGameId(q.game._id || q.game);
        }
      })
      .catch((error) => {
        alertError({
          error: error,
          customMessage: "Could not get quiz.",
        });
      });
  }, [quizId]);

  // Al cambiar el juego en el desplegable, cargar el quiz de ese juego para que las respuestas coincidan
  useEffect(() => {
    if (!selectedGameId || !quizData) return;
    const currentGameId = quizData.game?._id || quizData.game;
    if (currentGameId === selectedGameId) return;
    getQuizzes({ game: selectedGameId })
      .then((res) => {
        if (res.status !== 200 || !Array.isArray(res.data)) return;
        const scenarioId = quizData.scenario?._id || quizData.scenario;
        const fullName = quizData.fullName;
        const same = res.data.find(
          (q) => (q.scenario?._id || q.scenario) == scenarioId && (q.fullName || "") === (fullName || "")
        );
        if (same && same._id && same._id !== quizId) history.replace(`/quiz/${same._id}`);
      })
      .catch(() => {});
  }, [selectedGameId, quizData, quizId, history]);

  useEffect(() => {
    const params = selectedGameId ? { game: selectedGameId } : {};
    getAnswerByQuiz(quizId, params)
      .then((res) => {
        if (res.status === 200) {
          setData(getData(res.data || []));
          setRefresh(false);
        }
      })
      .catch((error) => {
        alertError({
          error: error,
          customMessage: "Could not get answers.",
        });
      });
  }, [quizId, refresh, selectedGameId]);

  return (
    <>
      <Card>
        <CardHeader title={`Quiz | ${quiz}`}>
          <CardHeaderToolbar>
            <div>
              Scenario <b>{scenario}</b>
            </div>
          </CardHeaderToolbar>
        </CardHeader>
        <CardBody>
          <div className="form-group mb-4">
            <label className="font-weight-bold d-block mb-2">Solicitar pruebas – contenido del quiz</label>
            <p className="text-muted small mb-2">
              Edita el nombre de cada prueba, si está justificada y la justificación que se muestra al pulsar (por qué está bien o mal).
            </p>
            <Button variant="outlined" color="primary" onClick={openEditPruebas}>
              Editar pruebas
            </Button>
          </div>

          <div className="form-group mb-4">
            <label htmlFor="quiz-answers-game-select" className="font-weight-bold">
              Game (filter answers)
            </label>
            <select
              id="quiz-answers-game-select"
              className="form-control"
              style={{ maxWidth: 320 }}
              value={selectedGameId}
              onChange={(e) => setSelectedGameId(e.target.value)}
            >
              <option value="">All games</option>
              {games.map((g) => (
                <option key={g._id} value={g._id}>
                  {g.fullName}
                </option>
              ))}
            </select>
            <small className="form-text text-muted">
              Answers are from groups. Select a game to see only answers from groups of that game.
            </small>
          </div>
          <Table data={data} columns={columns} defaultSorted={defaultSorted} />
        </CardBody>
      </Card>

      <Dialog open={editPruebasOpen} onClose={() => setEditPruebasOpen(false)} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle>{isScenario3Quiz(quizData) ? "Editar opciones (etiquetas e imágenes)" : "Editar pruebas (Solicitar pruebas)"}</DialogTitle>
        <DialogContent>
          <p className="text-muted small mb-3">
            {isScenario3Quiz(quizData) ? "Edita la etiqueta y la imagen de cada opción." : "Usa Editar en cada fila para cambiar nombre, si está justificada y la justificación."}
          </p>
          <div className="table-responsive">
            <table className="table table-bordered table-hover">
              <thead>
                <tr>
                  <th style={{ width: 80 }}>#</th>
                  <th>{isScenario3Quiz(quizData) ? "Etiqueta" : (isScenario52Quiz(quizData) || isScenario52Question2Quiz(quizData)) ? "Afirmación" : isScenario2Quiz(quizData) || isScenario5Quiz(quizData) ? "Justificación" : "Nombre"}</th>
                  {isScenario3Quiz(quizData) && <th style={{ width: 120 }} className="text-center">Imagen</th>}
                  {!isScenario3Quiz(quizData) && <th style={{ width: 120 }} className="text-center">{(isScenario52Quiz(quizData) || isScenario52Question2Quiz(quizData)) ? (isScenario52Question2Quiz(quizData) ? "¿Infiltrada?" : "V/F") : "Justificada"}</th>}
                  <th style={{ width: 100 }} className="text-center">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {editingPruebasOptions.map((opt, index) => (
                  <tr key={opt.id}>
                    <td>{index + 1}</td>
                    <td className={isScenario2Quiz(quizData) || (isScenario5Quiz(quizData) && !isScenario52Quiz(quizData) && !isScenario52Question2Quiz(quizData)) ? "text-truncate" : (isScenario52Quiz(quizData) || isScenario52Question2Quiz(quizData)) ? "text-truncate" : ""} style={(isScenario2Quiz(quizData) || (isScenario5Quiz(quizData) && !isScenario52Quiz(quizData) && !isScenario52Question2Quiz(quizData)) ? { maxWidth: 320 } : (isScenario52Quiz(quizData) || isScenario52Question2Quiz(quizData)) ? { maxWidth: 400 } : undefined)} title={(isScenario52Quiz(quizData) || isScenario52Question2Quiz(quizData)) ? explanationPreview(opt.text, 200) : isScenario2Quiz(quizData) || isScenario5Quiz(quizData) ? explanationPreview(opt.explanation, 200) : undefined}>
                      {isScenario3Quiz(quizData) ? (opt.text || "—") : (isScenario52Quiz(quizData) || isScenario52Question2Quiz(quizData)) ? explanationPreview(opt.text) : isScenario2Quiz(quizData) || isScenario5Quiz(quizData) ? explanationPreview(opt.explanation) : (opt.text || "—")}
                    </td>
                    {isScenario3Quiz(quizData) && (
                      <td className="text-center">
                        {opt.image ? <img src={buildImageUrl(opt.image)} alt="" style={{ maxHeight: 48, maxWidth: 80, objectFit: "contain" }} /> : <span className="text-muted">—</span>}
                      </td>
                    )}
                    {!isScenario3Quiz(quizData) && <td className="text-center">{isScenario52Quiz(quizData) ? (opt.correct ? "V" : "F") : (opt.correct ? "Sí" : "No")}</td>}
                    <td className="text-center">
                      <Tooltip title="Editar">
                        <Button size="small" onClick={() => openEditPruebaOne(index)}>
                          <EditIcon />
                        </Button>
                      </Tooltip>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditPruebasOpen(false)}>Cancelar</Button>
          <Button onClick={savePruebasOptions} color="primary" variant="contained" disabled={savingPruebas}>
            {savingPruebas ? "Guardando…" : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editingPruebaIndex != null} onClose={closeEditPruebaOne} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle>{isScenario3Quiz(quizData) ? "Editar opción (etiqueta e imagen)" : `Editar prueba ${editingPruebaIndex != null ? editingPruebaIndex + 1 : ""}`}</DialogTitle>
        <DialogContent>
          {editingPruebaOne && (
            <div className="py-2">
              {isScenario3Quiz(quizData) ? (
                <>
                  <div className="form-group">
                    <label className="small">Etiqueta</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editingPruebaOne.text || ""}
                      onChange={(e) => updateEditingPruebaOne("text", e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="small">Imagen</label>
                    <div className="d-flex align-items-center gap-3">
                      {editingPruebaOne.image && (
                        <img src={buildImageUrl(editingPruebaOne.image)} alt="" style={{ maxHeight: 120, maxWidth: 160, objectFit: "contain", border: "1px solid #ddd" }} />
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        className="form-control-file"
                        onChange={async (e) => {
                          const input = e.target;
                          const file = input?.files?.[0];
                          if (!file) return;
                          try {
                            const res = await uploadSingleFile(file, "scenario3-quiz");
                            if (res?.status === 200 && res.data?.fileURL) {
                              updateEditingPruebaOne("image", res.data.fileURL);
                            } else {
                              alertError({ customMessage: "No se pudo subir la imagen." });
                            }
                          } catch (err) {
                            alertError({ error: err, customMessage: "Error al subir la imagen." });
                          }
                          if (input) input.value = "";
                        }}
                      />
                      {editingPruebaOne.image && (
                        <Button size="small" color="secondary" onClick={() => updateEditingPruebaOne("image", "")} className="mt-1">
                          Quitar imagen
                        </Button>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {isScenario2Quiz(quizData) ? (
                    <div className="form-group">
                      <label className="small">Nombre</label>
                      <div className="form-control-plaintext font-weight-bold">Diagnóstico {editingPruebaIndex + 1}</div>
                    </div>
                  ) : (isScenario52Quiz(quizData) || isScenario52Question2Quiz(quizData)) ? (
                    <div className="form-group">
                      <label className="small">Afirmación (texto que ve el usuario)</label>
                      <Editor
                        key={`view-text-52-${editingPruebaOne.id}`}
                        body={editingPruebaOne.text || ""}
                        setBody={(v) => updateEditingPruebaOne("text", v)}
                        placeholder="Texto de la afirmación..."
                        lang="es"
                      />
                    </div>
                  ) : isScenario5Quiz(quizData) ? (
                    <div className="form-group">
                      <label className="small">Opción (en la web el orden es aleatorio y la letra a)/b)/c)/d) se asigna por posición)</label>
                      <div className="form-control-plaintext font-weight-bold">Opción {editingPruebaIndex + 1}</div>
                    </div>
                  ) : (
                    <div className="form-group">
                      <label className="small">Nombre</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editingPruebaOne.text || ""}
                        onChange={(e) => updateEditingPruebaOne("text", e.target.value)}
                      />
                    </div>
                  )}
                  <div className="form-group form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="view-edit-one-justified"
                      checked={!!editingPruebaOne.correct}
                      onChange={(e) => updateEditingPruebaOne("correct", e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="view-edit-one-justified">
                      {isScenario52Quiz(quizData) ? "Verdadera (desmarca si es Falsa)" : isScenario52Question2Quiz(quizData) ? "Es la afirmación infiltrada" : "Está justificada"}
                    </label>
                  </div>
                  <div className="form-group">
                    <label className="small">{(isScenario52Quiz(quizData) || isScenario52Question2Quiz(quizData)) ? (isScenario52Quiz(quizData) ? "Justificación (se muestra al completar; en falsas explica el dato correcto)" : "Justificación (opcional)") : "Justificación (solo contenido; la frase inicial se genera según si está justificada)"}</label>
                    <div style={{ minHeight: 120 }}>
                      <Editor
                        key={`view-expl-${editingPruebaOne.id}`}
                        body={editingPruebaOne.explanation || ""}
                        setBody={(v) => updateEditingPruebaOne("explanation", v)}
                        placeholder="HTML o texto de la justificación..."
                        lang="es"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="small">Confirmación previa (opcional)</label>
                    <div style={{ minHeight: 80 }}>
                      <Editor
                        key={`view-conf-${editingPruebaOne.id}`}
                        body={editingPruebaOne.confirmExplanation || ""}
                        setBody={(v) => updateEditingPruebaOne("confirmExplanation", v)}
                        placeholder="Opcional..."
                        lang="es"
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditPruebaOne}>Cancelar</Button>
          <Button onClick={saveEditPruebaOne} color="primary" variant="contained">
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
